/**
 * Generator opisów technicznych produktów używający AI
 * Tworzy profesjonalne opisy dla ofert
 */

import { CalculatorItem, CustomProduct } from '@/types/Offer';

interface ProductDescription {
  technical: string;
  marketing: string;
  specifications: string[];
  advantages: string[];
  applications: string[];
}

export class AIProductDescriptionGenerator {
  /**
   * Generuje profesjonalny opis produktu na podstawie danych
   */
  static generateDescription(item: CalculatorItem | CustomProduct): ProductDescription {
    // Sprawdź typ produktu
    if ('isCustom' in item && item.isCustom) {
      return this.generateCustomProductDescription(item as CustomProduct);
    }
    
    if ('isMagnet' in item && item.isMagnet) {
      return this.generateMagnetDescription(item);
    }
    
    return this.generateStandardProductDescription(item as CalculatorItem);
  }

  /**
   * Generuje opis dla standardowego produktu
   */
  private static generateStandardProductDescription(item: CalculatorItem): ProductDescription {
    const productTypes: Record<string, { name: string; category: string }> = {
      ekspozytor: { name: 'Ekspozytor', category: 'display' },
      formatka: { name: 'Formatka', category: 'sheet' },
      kaseton: { name: 'Kaseton reklamowy', category: 'lightbox' },
      ledon: { name: 'Led On', category: 'led' },
      podkladki: { name: 'Podkładki dystansowe', category: 'spacers' }
    };

    const product = productTypes[item.product || 'ekspozytor'];
    const dimensions = `${item.dimensions.width}×${item.dimensions.height}${item.dimensions.depth ? `×${item.dimensions.depth}` : ''} mm`;
    
    // Generuj opis techniczny
    const technical = this.generateTechnicalDescription(item, product);
    
    // Generuj opis marketingowy
    const marketing = this.generateMarketingDescription(item, product);
    
    // Generuj specyfikacje
    const specifications = this.generateSpecifications(item);
    
    // Generuj zalety
    const advantages = this.generateAdvantages(item, product);
    
    // Generuj zastosowania
    const applications = this.generateApplications(item, product);
    
    return {
      technical,
      marketing,
      specifications,
      advantages,
      applications
    };
  }

  /**
   * Generuje opis techniczny
   */
  private static generateTechnicalDescription(item: CalculatorItem, product: { name: string; category: string }): string {
    const descriptions: Record<string, string> = {
      display: `${product.name} wykonany z wysokiej jakości ${item.materialName || 'pleksi'} o grubości ${item.thickness}mm. 
        Konstrukcja zapewnia stabilność i wytrzymałość przy wymiarach ${item.dimensions.width}×${item.dimensions.height}${item.dimensions.depth ? `×${item.dimensions.depth}` : ''} mm. 
        ${item.expositorType ? `Typ ekspozytora: ${item.expositorType}. ` : ''}
        Produkt wykonany metodą cięcia laserowego z precyzją ±0.1mm, gwarantującą idealne dopasowanie wszystkich elementów.`,
      
      sheet: `Formatka z ${item.materialName || 'pleksi'} o grubości ${item.thickness}mm, wymiary ${item.dimensions.width}×${item.dimensions.height} mm. 
        Krawędzie polerowane diamentowo dla uzyskania kryształowej przejrzystości. 
        Materiał charakteryzuje się wysoką odpornością na promieniowanie UV oraz zachowaniem parametrów optycznych przez min. 10 lat.`,
      
      lightbox: `Kaseton reklamowy podświetlany LED, wykonany z ${item.materialName || 'pleksi'} ${item.thickness}mm. 
        Wymiary: ${item.dimensions.width}×${item.dimensions.height}×${item.dimensions.depth || 100} mm. 
        System podświetlenia LED zapewnia równomierne rozproszenie światła, energooszczędność (zużycie <30W) oraz żywotność >50000h.`,
      
      led: `System podświetlenia LED ON z ${item.materialName || 'pleksi'} ${item.thickness}mm. 
        Wymiary: ${item.dimensions.width}×${item.dimensions.height} mm. 
        Zintegrowane oświetlenie krawędziowe LED 12V z możliwością regulacji intensywności świecenia.`,
      
      spacers: `Podkładki dystansowe z ${item.materialName || 'pleksi'} ${item.thickness}mm. 
        Średnica: ${item.dimensions.width}mm, wysokość: ${item.dimensions.height}mm. 
        Precyzyjne wykonanie CNC zapewnia tolerancję wymiarową ±0.05mm.`
    };
    
    return descriptions[product.category] || descriptions.display;
  }

  /**
   * Generuje opis marketingowy
   */
  private static generateMarketingDescription(item: CalculatorItem, product: { name: string; category: string }): string {
    const descriptions: Record<string, string> = {
      display: `Elegancki i funkcjonalny ${product.name.toLowerCase()} który wyróżni Twoje produkty na tle konkurencji. 
        Nowoczesny design i krystaliczna przejrzystość materiału przyciągają wzrok klientów, 
        zwiększając widoczność i atrakcyjność prezentowanych towarów nawet o 40%.`,
      
      sheet: `Wysokiej jakości formatka idealna do profesjonalnych zastosowań. 
        Doskonała przejrzystość optyczna i gładkość powierzchni sprawiają, że jest to idealny wybór 
        dla wymagających projektów wystawienniczych i reklamowych.`,
      
      lightbox: `Przyciągający uwagę kaseton reklamowy, który sprawi, że Twoja marka będzie widoczna 24/7. 
        Energooszczędne podświetlenie LED i wysokiej jakości wykonanie gwarantują 
        niskie koszty eksploatacji przy maksymalnym efekcie wizualnym.`,
      
      led: `Innowacyjny system podświetlenia, który nada Twoim produktom wyjątkowy charakter. 
        Subtelne, eleganckie oświetlenie LED podkreśla jakość i prestiż prezentowanych elementów.`,
      
      spacers: `Profesjonalne podkładki dystansowe zapewniające perfekcyjną prezentację. 
        Niewidoczne wsparcie, które pozwala na efekt "lewitacji" eksponowanych elementów.`
    };
    
    return descriptions[product.category] || descriptions.display;
  }

  /**
   * Generuje specyfikacje techniczne
   */
  private static generateSpecifications(item: CalculatorItem): string[] {
    const specs = [
      `Materiał: ${item.materialName || 'Pleksi bezbarwna'}`,
      `Grubość: ${item.thickness} mm`,
      `Wymiary: ${item.dimensions.width} × ${item.dimensions.height}${item.dimensions.depth ? ` × ${item.dimensions.depth}` : ''} mm`,
      `Ilość: ${item.quantity} szt.`,
      `Waga jednostkowa: ${((item.calculations?.totalWeight || 0) / item.quantity).toFixed(2)} kg`,
      `Metoda obróbki: Cięcie laserowe CNC`
    ];
    
    // Dodaj opcje jeśli są
    if (item.options) {
      if (item.options.wieko) specs.push('✓ Wieko zabezpieczające');
      if (item.options.grawerunek) specs.push('✓ Grawerowanie laserowe');
      if (item.options.nadruk) specs.push('✓ Nadruk UV');
      if (item.options.giecie) specs.push('✓ Gięcie na gorąco');
      if (item.options.klejenieUV) specs.push('✓ Klejenie UV');
      if (item.options.frezy) specs.push('✓ Frezowanie CNC');
      if (item.options.otwory) specs.push('✓ Otwory montażowe');
    }
    
    return specs;
  }

  /**
   * Generuje listę zalet
   */
  private static generateAdvantages(item: CalculatorItem, product: { name: string; category: string }): string[] {
    const baseAdvantages = [
      '✓ Wysokiej jakości materiał z 10-letnią gwarancją',
      '✓ Precyzyjne wykonanie CNC (tolerancja ±0.1mm)',
      '✓ Kryształowa przejrzystość i połysk',
      '✓ Odporność na promieniowanie UV',
      '✓ Łatwa konserwacja i czyszczenie',
      '✓ Możliwość personalizacji (logo, kolory)'
    ];
    
    const categoryAdvantages: Record<string, string[]> = {
      display: [
        '✓ Stabilna konstrukcja',
        '✓ Modułowa budowa umożliwiająca rozbudowę',
        '✓ Elegancki wygląd pasujący do każdego wnętrza'
      ],
      lightbox: [
        '✓ Energooszczędne oświetlenie LED',
        '✓ Równomierne podświetlenie powierzchni',
        '✓ Żywotność LED >50000 godzin'
      ],
      led: [
        '✓ Efektowne podświetlenie krawędziowe',
        '✓ Niskie zużycie energii (<30W)',
        '✓ Możliwość regulacji jasności'
      ]
    };
    
    return [...baseAdvantages, ...(categoryAdvantages[product.category] || categoryAdvantages.display)];
  }

  /**
   * Generuje zastosowania
   */
  private static generateApplications(item: CalculatorItem, product: { name: string; category: string }): string[] {
    const applications: Record<string, string[]> = {
      display: [
        '• Sklepy i butiki',
        '• Targi i wystawy',
        '• Showroomy i salony',
        '• Apteki i drogerie',
        '• Biura i recepcje',
        '• Muzea i galerie'
      ],
      sheet: [
        '• Osłony i przegrody',
        '• Tablice informacyjne',
        '• Elementy wystawiennicze',
        '• Zabezpieczenia',
        '• Projekty DIY',
        '• Prototypowanie'
      ],
      lightbox: [
        '• Reklama zewnętrzna',
        '• Oznakowanie sklepów',
        '• Systemy informacyjne',
        '• Wystawy targowe',
        '• Dekoracje wnętrz',
        '• Podświetlane logo'
      ],
      led: [
        '• Ekskluzywne ekspozycje',
        '• Witryny jubilerskie',
        '• Prezentacje produktów premium',
        '• Showroomy',
        '• Wystawy muzealne',
        '• Dekoracje eventowe'
      ],
      spacers: [
        '• Montaż tablic',
        '• Systemy wystawiennicze',
        '• Konstrukcje wielopoziomowe',
        '• Instalacje artystyczne',
        '• Meble z pleksi',
        '• Elementy dystansowe'
      ]
    };
    
    return applications[product.category] || applications.display;
  }

  /**
   * Generuje opis dla produktu nietypowego
   */
  private static generateCustomProductDescription(item: CustomProduct): ProductDescription {
    return {
      technical: `Produkt wykonany według indywidualnej specyfikacji klienta. ${item.description}. 
        Realizacja z zachowaniem najwyższych standardów jakości i precyzji wykonania charakterystycznych dla PlexiSystem.`,
      
      marketing: `Unikalne rozwiązanie dostosowane do Twoich potrzeb. 
        Nasz zespół ekspertów zadba o każdy szczegół, aby finalny produkt spełnił wszystkie Twoje oczekiwania.`,
      
      specifications: [
        `Nazwa: ${item.name}`,
        `Ilość: ${item.quantity} szt.`,
        `Cena jednostkowa: ${item.unitPrice.toFixed(2)} PLN`,
        'Produkt nietypowy - wykonanie na zamówienie'
      ],
      
      advantages: [
        '✓ Indywidualne podejście do projektu',
        '✓ Konsultacje techniczne na każdym etapie',
        '✓ Gwarancja jakości PlexiSystem',
        '✓ Możliwość modyfikacji w trakcie realizacji'
      ],
      
      applications: [
        '• Rozwiązania na miarę',
        '• Projekty specjalne',
        '• Nietypowe konstrukcje',
        '• Indywidualne zamówienia'
      ]
    };
  }

  /**
   * Generuje opis dla magnesów
   */
  private static generateMagnetDescription(item: any): ProductDescription {
    return {
      technical: `Magnesy neodymowe ${item.magnetType} o wymiarach Ø${item.diameter}×${item.thickness}mm. 
        Siła przyciągania dostosowana do wagi ekspozytora i grubości blachy montażowej. 
        Powłoka niklowa zapewniająca ochronę przed korozją i uszkodzeniami mechanicznymi.`,
      
      marketing: `Profesjonalne magnesy neodymowe zapewniające pewne i bezpieczne mocowanie ekspozytorów. 
        Najwyższa jakość i trwałość gwarantowana przez wiodącego producenta.`,
      
      specifications: [
        `Typ: ${item.magnetType}`,
        `Wymiary: Ø${item.diameter} × ${item.thickness} mm`,
        `Ilość: ${item.quantity} szt.`,
        `Powłoka: Nikiel (Ni-Cu-Ni)`,
        `Temperatura pracy: -40°C do +80°C`
      ],
      
      advantages: [
        '✓ Ekstremalnie silne przyciąganie',
        '✓ Odporne na korozję',
        '✓ Długotrwała siła magnetyczna',
        '✓ Łatwy montaż i demontaż',
        '✓ Nie pozostawiają śladów'
      ],
      
      applications: [
        '• Mocowanie ekspozytorów na regałach metalowych',
        '• Systemy wystawiennicze',
        '• Tablice magnetyczne',
        '• Mocowania tymczasowe',
        '• Rozwiązania targowe'
      ]
    };
  }
}
